import React, { useState, useEffect, startTransition } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
  Fab
} from '@mui/material';
import { 
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Support as SupportIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  People as PatientsIcon,
  CalendarMonth as AppointmentsIcon,
  LocalHospital as DoctorsIcon,
  Dashboard as DashboardIcon,
  PeopleAlt as StaffIcon,
  LocalHospital,
  Chat as ChatIcon,
  Campaign as CampaignIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { FaNotesMedical } from "react-icons/fa6";
import { FaHandHoldingMedical } from "react-icons/fa";

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SPBottomNavBar from '../components/serviceprovider/SPBottomNavBar';
import { serviceProviderPortalAPI } from '../services/api';

const DRAWER_WIDTH = 250;

const MenuItemWithIcon = ({ icon, text, onClick }) => (
  <MenuItem onClick={onClick}>
    <ListItemIcon>
      {icon}
    </ListItemIcon>
    <ListItemText primary={text} />
  </MenuItem>
);

const SideNavItem = ({ icon, text, path, selected, onClick }) => {
  const theme = useTheme();
  
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          minHeight: 48,
          mx: 2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&::before': selected ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            borderRadius: '0 4px 4px 0',
            backgroundColor: theme.palette.primary.dark,
            boxShadow: `0 0 12px ${alpha(theme.palette.primary.dark, 0.5)}`
          } : {},
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            backdropFilter: 'blur(20px)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.dark, 0.12),
            }
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.dark, 0.06),
            transform: 'translateX(4px)',
            '& .MuiListItemIcon-root': {
              transform: 'scale(1.1)',
            }
          }
        }}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: 40,
            color: selected ? theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.6),
            transition: 'all 0.2s ease-in-out',
            transform: selected ? 'scale(1.1)' : 'scale(1)',
            '& svg': {
              fontSize: '1.3rem'
            }
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={text} 
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: selected ? 600 : 500,
            color: selected ? theme.palette.primary.dark : theme.palette.text.primary,
            sx: {
              transition: 'all 0.2s ease-in-out',
              opacity: selected ? 1 : 0.8
            }
          }}
        />
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              right: -10,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              animation: 'pulse 2s infinite'
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};

// This layout adds bottom navigation to all Service Provider pages
const ServiceProviderLayout = ({ children, title = 'Service Provider Dashboard' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Check user type with robust fallbacks to avoid undefined access
  const userType = user?.user_type ?? user?.user?.user_type ?? null;
  const isSpAdmin = userType === 'sp_admin';
  const isSpStaff = userType === 'sp_staff';

  const hasPendingMembership = false; // Placeholder; will be set from localStorage flag below
  const [pendingMembership, setPendingMembership] = useState(false);

  React.useEffect(() => {
    // Read a lightweight flag set by membership page when payment_status != 'paid'
    const flag = localStorage.getItem('sp_membership_payment_pending') === '1';
    setPendingMembership(flag);
  }, [location.pathname]);

  const navigationItems = [
    { path: '/sp/dashboard', text: 'Home', icon: <HomeIcon /> },
    { path: '/sp/patients', text: 'Patients', icon: <PatientsIcon /> },
    { path: '/sp/appointments', text: 'Appointments', icon: <AppointmentsIcon /> },
    { path: '/sp/doctors', text: 'Doctors', icon: <DoctorsIcon /> },
    ...(isSpAdmin ? [{ path: '/sp/staff-management', text: 'Staff', icon: <StaffIcon /> }] : []),
    { path: '/sp/tickets', text: 'Support', icon: <SupportIcon /> },
    ...(isSpAdmin ? [
      { path: '/sp/advertisements', text: 'Advertisements', icon: <CampaignIcon /> },
      { path: '/sp/memberships', text: 'Membership', icon: pendingMembership ? <WarningAmberIcon sx={{ color: theme.palette.warning.main }} /> : <FaNotesMedical /> },
    ] : []),
  ];
  
  // Fetch unread notification count
  const fetchUnreadCount = async () => {
    try {
      const response = await serviceProviderPortalAPI.getUnreadCount();
      let count = 0;
      if (typeof response.data === 'number') {
        count = response.data;
      } else if (response.data && typeof response.data.unread_count === 'number') {
        count = response.data.unread_count;
      } else if (response.data && typeof response.data === 'object') {
        count = Object.values(response.data)[0] || 0;
      }
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching service provider notification count:', error);
      setUnreadCount(0);
    }
  };
  
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleProfileOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const navigateToProfile = () => {
    startTransition(() => {
      if (isSpStaff) {
        navigate('/sp/staff/profile');
      } else {
        navigate('/sp/profile');
      }
      handleProfileClose();
    });
  };
  
  const navigateToSettings = () => {
    startTransition(() => {
      navigate('/sp/tickets');
      handleProfileClose();
    });
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      startTransition(() => {
        navigate('/sp/login', { replace: true });
      });
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      startTransition(() => {
        navigate('/sp/login', { replace: true });
      });
    }
  };

  const handleNotificationClick = () => {
    startTransition(() => {
      if (isSpStaff) {
        navigate('/sp/staff/notifications');
      } else {
        navigate('/sp/notifications');
      }
    });
  };

  const renderSideNav = () => (
    <Drawer
      variant="permanent"
      elevation={0}
      sx={{
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: `none`,
          background: alpha(theme.palette.neutral.light, 1),
          // background: `linear-gradient(-385deg, ${alpha(theme.palette.primary.main, 0.3)} 40%, ${alpha(theme.palette.secondary.dark, 0.3)} 50%)`,
          // backdropFilter: 'blur(10px)',
          // WebkitBackdropFilter: 'blur(10px)',
       
          // boxShadow: `4px 0 8px ${alpha(theme.palette.common.black, 0.05)}`,
        },
      }}
    >
      {/* Logo Section */}
      {/* <Box
        sx={{
          p: 1.4,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: `linear-gradient(135deg, 
            ${alpha(theme.palette.primary.light, 0.3)} , 
            ${alpha(theme.palette.secondary.dark, 0.3)} 
          )`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backdropFilter: 'blur(20px)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: `linear-gradient(to right, 
              transparent,
              ${alpha(theme.palette.primary.main, 0.2)},
              transparent
            )`
          }
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'transparent',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            backdropFilter: 'blur(20px)',
            '& img': {
              width: '70%',
              height: '70%',
              objectFit: 'contain'
            }
          }}
        >
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.primary.main,
              textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            E
          </Typography>
        </Avatar>
        <Box>
          <Typography 
           
            sx={{ 
              fontWeight: 900,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.5px'
            }}
          >
            Easy Health
          </Typography>
          <Typography 
            variant="caption"
            sx={{ 
              opacity: 0.8,
              display: 'block',
              marginTop: '-2px'
            }}
          >
            Digital Solutions
          </Typography>
        </Box>
      </Box> */}

      {/* Navigation Section */}
      
      <Box
        sx={{
          mt: 2,
          px: 2,
         
          '& .MuiList-root': {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '20%',
              right: '20%',
              height: '1px',
              background: `linear-gradient(to right, 
                transparent,
                ${alpha(theme.palette.primary.main, 0.3)},
                transparent
              )`
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <FaHandHoldingMedical style={{
            fontSize: '2.5em',
            color: theme.palette.primary.main,
            marginBottom: '12px',
            // filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.4)})`,
            transition: 'all 0.2s ease-in-out'
          }} />
          <Typography 
            variant="overline"
            sx={{
              display: 'block',
              color: alpha(theme.palette.primary.dark, 1),
              fontWeight: 900,
              letterSpacing: '1px',
              textAlign: 'center',
              marginBottom: 1,
              fontSize: '0.8rem'
            }}
          >
            Easy Health<br/> Digital Solutions
          </Typography>
        </Box>
        {/* <Typography
          variant="overline"
          sx={{
            px: 3,
            py: 1.5,
            mt: 2,
            display: 'block',
            color: alpha(theme.palette.text.primary, 0.6),
            fontWeight: 500,
            letterSpacing: '1px'
          }}
        >
          MAIN MENU
        </Typography> */}
        <List>
          {navigationItems.map((item) => (
            <SideNavItem
              key={item.path}
              icon={item.icon}
              text={item.text}
              path={item.path}
              selected={location.pathname === item.path}
              onClick={() => startTransition(() => navigate(item.path))}
            />
          ))}
        </List>
      </Box>

      {/* User Section at Bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          // p: 2,
          background: `linear-gradient(-145deg, ${alpha(theme.palette.secondary.dark, 0.2)} 20%, ${alpha(theme.palette.primary.light, 0.2)} 80%)`,
          backdropFilter: 'blur(20px)',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
         
          
        }}
      >
        <Box
          sx={{
            p: 2,
            pt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.08),
              transform: 'translateY(-2px)'
            }
          }}
          onClick={() => {
            startTransition(() => {
              if (isSpStaff) {
                navigate('/sp/staff/profile');
              } else {
                navigate('/sp/profile');
              }
            });
          }}
        >   
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: theme.palette.primary.main
            }}
          >
            {user?.name ? user.name[0].toUpperCase() : 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.user?.email || 'No email'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Side Navigation */}
      {isDesktop && renderSideNav()}

      {/* Main Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }
      }}>
        {/* Header */}
        <AppBar 
          position="sticky" 
          sx={{
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            ml: { md: `${DRAWER_WIDTH}px` },
            justifyContent: { xs: 'space-between', md: 'space-between',lg: 'flex-end' },
            alignItems: { xs: 'space-between', md: 'space-between',lg: 'flex-end' },
            background: alpha(theme.palette.neutral.light, 1),
            boxShadow: 'none',
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.primary.dark,
                display: { xs: 'block', md: 'none' } // Show only on mobile, hide on desktop
              }}
            >
              Easy Health Digital Solutions
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={handleNotificationClick}
                sx={{ color: theme.palette.primary.dark }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: 18,
                      minWidth: 18
                    }
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              
              <IconButton color="inherit" onClick={handleProfileOpen}>
                <Avatar sx={{ bgcolor: theme.palette.primary.dark }}>
                  {user?.name?.charAt(0) || 'S'}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileClose}
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 180,
              borderRadius: 2,
              mt: 1.5,
              

            }
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {user?.name || 'Service Provider'}
            </Typography>
          </Box>
          <Divider />
          <MenuItemWithIcon 
            icon={<PersonIcon fontSize="small" />} 
            text="Profile" 
            onClick={navigateToProfile} 
          />
          {/* <MenuItemWithIcon 
            icon={<SettingsIcon fontSize="small" />} 
            text="Support" 
            onClick={navigateToSettings} 
          /> */}
          <Divider />
          <MenuItemWithIcon 
            icon={<LogoutIcon fontSize="small" />} 
            text="Logout" 
            onClick={handleLogout} 
          />
        </Menu>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          pb: { xs: 7, md: 3 },
          background:theme.palette.common.white,
          position: 'relative'  // Add this to ensure proper positioning of FAB
        }}>
          {children}
          
          {/* Floating Action Button */}
          <Fab
            size="small"
            aria-label="chat"
            onClick={() => {
              startTransition(() => {
                if (isSpStaff) {
                  navigate('/sp/staff/tickets');
                } else {
                  navigate('/sp/tickets');
                }
              });
            }}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, md: 40,lg: 40 }, // Adjust bottom position based on screen size
              right: {xs: 12, md: 12,lg: 32}, 
              color: theme.palette.common.white,
              background:theme.palette.primary.dark,
              boxShadow: (theme) => `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                background:theme.palette.primary.light,
                color:theme.palette.primary.main,
                transform: 'translateY(-4px)',
                boxShadow: (theme) => `0 12px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
              transition: 'all 0.3s ease-in-out'
            }}
          >
            <ChatIcon  />
          </Fab>
        </Box>
        
        {/* Bottom Navigation - Only show on mobile */}
        {!isDesktop && <SPBottomNavBar pendingNotifications={unreadCount} />}
      </Box>
    </Box>
  );
};

export default ServiceProviderLayout; 